/**
 * Registre des réglages du site.
 *
 * **Source unique** : la table `settings` ne stocke que des couples clé/valeur
 * en texte, sans rien dire de leur nature. Ce fichier porte le sens — type,
 * libellé, valeur par défaut, exposition publique — et trois usages en
 * dérivent sans jamais se recopier :
 *
 * - le **formulaire d'administration**, qui construit ses onglets et ses champs
 *   à partir des groupes et des types déclarés ici ;
 * - la **validation serveur**, qui refuse une clé absente du registre et une
 *   valeur qui ne correspond pas à son type ;
 * - la **lecture typée** (`getSetting`), qui rend un `number` là où un `number`
 *   est déclaré, et non la chaîne brute lue en base.
 *
 * Ajouter un réglage se fait donc **ici et nulle part ailleurs** : aucune
 * migration, aucun champ à câbler dans l'écran. Même principe que
 * `site-palette.ts` pour les couleurs et `system-pages.ts` pour les pages
 * piliers.
 *
 * ⚠️ Une clé est un identifiant durable, écrit en base. La renommer orpheline
 * la ligne existante et fait retomber le réglage sur sa valeur par défaut : on
 * ne renomme pas une clé sans migration de données.
 */

import type { LinkMode, LinkValue } from './link-values'

/** Nature d'un réglage, qui détermine son champ de saisie et sa validation. */
export const settingTypes = [
  'text',
  'textarea',
  'number',
  'boolean',
  'url',
  'email',
  'datetime',
  'select',
  'link',
  'code',
] as const

export type SettingType = typeof settingTypes[number]

/**
 * Valeur d'un réglage une fois décodée, telle que la manipulent l'écran
 * d'administration et les consommateurs de `getSetting`.
 *
 * `null` signale un réglage jamais renseigné, ou vidé : c'est une valeur
 * légitime pour tout réglage non requis, et le site public doit la traiter
 * comme « rien à afficher ».
 *
 * Le type `link` fait exception à la nature scalaire des autres : sa valeur est
 * l'objet `LinkValue` déjà utilisé par les boutons de bandeau. Il est stocké
 * sérialisé en JSON — la colonne reste du texte —, et `decodeSettingValue` le
 * relit. Le partage avec les CTA n'est pas cosmétique : c'est lui qui donne au
 * réglage le choix interne/externe, l'ouverture dans un onglet et le suivi
 * d'une cible renommée, sans réécrire cette mécanique une seconde fois.
 */
/**
 * Le type `code` est une chaîne comme les autres pour le stockage, mais son
 * contenu est du **HTML inséré tel quel** dans les pages du site : scripts de
 * mesure d'audience, feuilles de style, balises de vérification. Il échappe
 * donc, seul de tout le registre, à l'échappement que subit un `textarea` — ce
 * qui est sa raison d'être, et ce qui en fait le réglage le plus sensible du
 * back-office. Voir le groupe `scripts`.
 */
export type SettingValue = string | number | boolean | LinkValue | null

export interface SettingOption {
  value: string
  label: string
}

export interface SettingDefinition {
  /** Clé stockée en base, préfixée par son groupe (`general.site_name`) */
  key: string
  group: SettingGroupKey
  type: SettingType
  /** Libellé du champ dans l'administration */
  label: string
  /** Texte d'aide affiché sous le champ */
  help?: string
  /** Valeur retenue tant que le réglage n'a jamais été enregistré */
  default: SettingValue
  /**
   * Réglage servi par l'API publique.
   *
   * `false` retient la valeur côté administration : `contact.form_recipient`
   * est une adresse interne, la publier l'offrirait aux robots de collecte.
   */
  public: boolean
  /** Longueur maximale, pour les types `text`, `textarea`, `url` et `email` */
  maxLength?: number
  /** Bornes, pour le type `number` */
  min?: number
  max?: number
  /** Valeurs autorisées, pour le type `select` */
  /**
   * Formes de destination proposées, pour un réglage `link`.
   *
   * Vide : le contenu du site et l'adresse extérieure. Le préciser sert à
   * ouvrir la médiathèque — un bouton de bannière renvoie parfois vers un
   * document — ou à fermer une forme qui n'aurait pas de sens : la politique
   * de confidentialité est une page du site, et rien d'autre.
   */
  modes?: LinkMode[]
  options?: SettingOption[]
  /** Nombre de lignes du champ, pour le type `textarea` */
  rows?: number
  /** Texte grisé du champ vide */
  placeholder?: string
  /**
   * Sous-section de l'onglet à laquelle le champ appartient.
   *
   * Purement visuelle : elle ne change ni la clé, ni l'enregistrement, qui
   * reste groupé par onglet. Un onglet qui mêle des réglages de nature
   * différente — le message d'une bannière, le bouton qui l'accompagne, la
   * période où elle paraît — se lit mal en liste plate ; les sections posent
   * les démarcations que le rédacteur attend.
   *
   * Un champ sans section ouvre l'onglet, avant le premier titre. Les champs
   * d'une même section doivent se suivre dans le registre : l'ordre
   * d'affichage est celui de la déclaration, et une section interrompue
   * apparaîtrait deux fois.
   */
  section?: string
}

/**
 * Titre et sous-titre d'une sous-section, indexés par son identifiant.
 *
 * Séparés des définitions de champs : plusieurs champs partagent une section,
 * et répéter son libellé sur chacun d'eux inviterait à les faire diverger.
 */
export interface SettingSectionDefinition {
  key: string
  label: string
  /** Phrase d'aide affichée sous le titre de la section. */
  description?: string
}

export interface SettingGroupDefinition {
  key: string
  /** Titre de l'onglet dans l'écran des réglages */
  label: string
  icon: string
  /** Phrase d'introduction affichée en tête de l'onglet */
  description?: string
}

/**
 * Groupes de réglages, dans l'ordre des onglets de l'écran d'administration.
 *
 * Le groupe est aussi la granularité d'enregistrement : chaque onglet a son
 * propre bouton et n'écrit que ses clés.
 */
export const settingGroups = [
  {
    key: 'general',
    label: 'Informations générales',
    icon: 'i-lucide-building',
    description: 'Coordonnées de l\'association, affichées dans le pied de page et sur la page Contact.',
  },
  {
    key: 'contact',
    label: 'Formulaire de contact',
    icon: 'i-lucide-mail',
    description: 'Destination des messages envoyés depuis le formulaire de contact du site.',
  },
  {
    key: 'social',
    label: 'Réseaux sociaux',
    icon: 'i-lucide-share-2',
    description: 'Un réseau laissé vide n\'apparaît pas sur le site.',
  },
  {
    key: 'seo',
    label: 'Référencement',
    icon: 'i-lucide-search',
    description: 'Valeurs reprises par les pages qui n\'en définissent pas, et accès des moteurs de recherche au site.',
  },
  {
    key: 'alert',
    label: 'Bannière d\'alerte',
    icon: 'i-lucide-triangle-alert',
    description: 'Bandeau affiché en haut de toutes les pages du site. Une seule bannière à la fois.',
  },
  {
    key: 'cookies',
    label: 'Bandeau cookies',
    icon: 'i-lucide-cookie',
    description: 'Demande son consentement au visiteur avant tout dépôt de cookie non essentiel.',
  },
  {
    key: 'scripts',
    label: 'Scripts et intégrations',
    icon: 'i-lucide-code',
    description: 'Code inséré dans toutes les pages du site : mesure d\'audience, styles, balises de vérification.',
  },
  {
    key: 'maintenance',
    label: 'Mode maintenance',
    icon: 'i-lucide-construction',
    description: 'Ferme le site aux visiteurs pendant une mise à jour. Le back-office reste accessible.',
  },
    {
      /*
       * Un onglet d'action, non de réglages : rien à enregistrer, un bouton qui
       * agit. Il vit ici parce que c'est là qu'on le cherche — au même endroit
       * que le mode maintenance, l'autre levier global sur le site.
       */
      key: 'cache',
      label: 'Cache du site',
      icon: 'i-lucide-refresh-cw',
      description: 'Vide les pages mises en réserve par le site, quand elles restent figées.',
    },
] as const satisfies readonly SettingGroupDefinition[]

export type SettingGroupKey = typeof settingGroups[number]['key']

export const settingGroupKeys = settingGroups.map(group => group.key) as SettingGroupKey[]

/** Types de bannière, et leur libellé non technique côté administration. */
/**
 * Sous-sections des onglets de réglages, indexées par groupe.
 *
 * Les déclarent les onglets qui réunissent plusieurs sujets distincts sous un
 * même bouton d'enregistrement : la bannière d'alerte et le bandeau cookies.
 */
export const settingSections: Record<string, SettingSectionDefinition[]> = {
  seo: [
    {
      key: 'defaults',
      label: 'Valeurs par défaut',
      description: 'Reprises par les pages qui ne définissent pas les leurs.',
    },
    {
      key: 'robots',
      label: 'Accès des moteurs de recherche',
      description: 'Commande le fichier `robots.txt` servi par le site, que les moteurs lisent avant de parcourir les pages.',
    },
  ],
  alert: [
    {
      key: 'banner',
      label: 'La bannière',
      description: 'Ce qui s\'affiche en haut de toutes les pages du site.',
    },
    {
      key: 'button',
      label: 'Le bouton',
      description: 'Optionnel. Laisser le lien vide pour une bannière sans bouton.',
    },
    {
      key: 'period',
      label: 'La période de diffusion',
      description: 'Optionnelle. Sans dates, la bannière paraît tant qu\'elle est activée.',
    },
  ],
  scripts: [
    {
      key: 'always',
      label: 'Chargé sur toutes les pages',
      description: 'Sans condition, dès l\'ouverture du site. À réserver à ce qui ne dépose aucun cookie : styles, polices, balises de vérification.',
    },
    {
      key: 'consent',
      label: 'Chargé après acceptation des cookies',
      description: 'N\'est inséré que si le visiteur a cliqué « Tout accepter » dans le bandeau. C\'est ici que vont Google Analytics, Meta Pixel et les autres traceurs.',
    },
  ],
  cookies: [
    {
      key: 'message',
      label: 'Le message',
      description: 'Ce que lit le visiteur à sa première visite, avant tout dépôt de cookie non essentiel.',
    },
    {
      key: 'buttons',
      label: 'Les boutons',
      description: 'Refuser doit être aussi accessible qu\'accepter : la CNIL demande deux choix de même poids.',
    },
    {
      key: 'display',
      label: 'L\'affichage',
      description: 'Où le bandeau se place, et combien de temps le choix du visiteur est retenu.',
    },
  ],
}

/**
 * Vrai quand le champ tient un état de saisie distinct de la valeur enregistrée.
 *
 * Seul le type `link` est dans ce cas : `LinkField` exige un `LinkValue`
 * complet, là où le réglage vaut `null` tant qu'aucune destination n'est
 * choisie. Le champ maintient donc une copie locale, synchronisée dans les deux
 * sens avec la valeur du formulaire.
 *
 * Cette synchronisation doit rester **strictement** réservée à ce type. Le
 * composant de champ rend tous les types depuis un seul fichier : armée sans
 * condition, elle réagissait au changement d'un `select`, réalignait le lien
 * sur une valeur vide, et cette écriture en retour effaçait le choix qu'on
 * venait de faire — le champ se vidait aussitôt rempli, et il fallait choisir
 * deux fois. D'où cette règle nommée plutôt qu'une comparaison écrite dans le
 * composant : elle porte la raison, et un test la retient.
 */
export function champASaisieLocale(definition: SettingDefinition): boolean {
  return definition.type === 'link'
}

/** Sous-sections d'un groupe, dans l'ordre d'affichage. */
export function getGroupSections(group: string): SettingSectionDefinition[] {
  return settingSections[group] ?? []
}

/** Emplacements possibles du bandeau cookies sur la page. */
export const cookiePositions: SettingOption[] = [
  { value: 'bottom', label: 'Bandeau en bas de page' },
  { value: 'bottom-left', label: 'Encart en bas à gauche' },
  { value: 'bottom-right', label: 'Encart en bas à droite' },
]

export const alertTypes: SettingOption[] = [
  { value: 'info', label: 'Information (bleu)' },
  { value: 'warning', label: 'Avertissement (orange)' },
  { value: 'danger', label: 'Urgent (rouge)' },
  { value: 'success', label: 'Succès (vert)' },
]

/**
 * Définition de chaque réglage.
 *
 * L'ordre à l'intérieur d'un groupe est celui des champs dans le formulaire.
 */
export const siteSettings = [
  // ── Informations générales ──
  {
    key: 'general.site_name',
    group: 'general',
    type: 'text',
    label: 'Nom du site',
    default: 'ACC Sud-Ouest',
    public: true,
    maxLength: 120,
  },
  {
    key: 'general.legal_name',
    group: 'general',
    type: 'text',
    label: 'Dénomination complète',
    help: 'Nom officiel de l\'association, affiché sous le logo dans le pied de page et sur la page Contact.',
    /*
     * Distincte du nom du site, et pas par excès de zèle : « ACC Sud-Ouest »
     * titre les onglets et les partages, où la dénomination entière serait
     * illisible ; celle-ci identifie l'association là où elle se présente.
     *
     * Elle était écrite en dur à deux endroits, dans deux formulations
     * différentes — le pied de page y forçait même un retour à la ligne.
     */
    default: 'Association des Copropriétaires et des Colotis du Sud-Ouest',
    public: true,
    maxLength: 190,
  },
  {
    key: 'general.phone',
    group: 'general',
    type: 'text',
    label: 'Téléphone',
    default: null,
    public: true,
    maxLength: 40,
    placeholder: '05 56 00 00 00',
  },
  {
    key: 'general.address',
    group: 'general',
    type: 'textarea',
    label: 'Adresse postale',
    default: null,
    public: true,
    maxLength: 500,
    rows: 3,
  },
  {
    key: 'general.email',
    group: 'general',
    type: 'email',
    label: 'Email de contact',
    help: 'Adresse de l\'association, reprise en pied des courriels envoyés depuis le site. Elle n\'est pas affichée sur les pages publiques : le formulaire de contact y tient ce rôle.',
    default: null,
    // Servie au site, cette adresse finissait en clair dans le HTML de la page
    // de contact — la première que visitent les robots de collecte. Le serveur
    // la garde pour signer les courriels qu'il envoie ; le visiteur qui veut
    // écrire passe par le formulaire, qui atteint la même boîte.
    public: false,
    maxLength: 190,
  },

  // ── Formulaire de contact ──
  {
    key: 'contact.form_recipient',
    group: 'contact',
    type: 'email',
    label: 'Email destinataire du formulaire',
    help: 'Les messages du formulaire de contact arrivent à cette adresse. Elle n\'est jamais affichée sur le site.',
    default: null,
    // Adresse de réception interne : la publier l'exposerait aux robots de
    // collecte, alors que seul le serveur en a besoin pour router le message.
    public: false,
    maxLength: 190,
  },

  // ── Réseaux sociaux ──
  {
    key: 'social.facebook',
    group: 'social',
    type: 'url',
    label: 'Facebook',
    default: null,
    public: true,
    maxLength: 255,
    placeholder: 'https://www.facebook.com/…',
  },
  {
    key: 'social.instagram',
    group: 'social',
    type: 'url',
    label: 'Instagram',
    default: null,
    public: true,
    maxLength: 255,
    placeholder: 'https://www.instagram.com/…',
  },
  {
    key: 'social.linkedin',
    group: 'social',
    type: 'url',
    label: 'LinkedIn',
    default: null,
    public: true,
    maxLength: 255,
    placeholder: 'https://www.linkedin.com/…',
  },
  {
    key: 'social.youtube',
    group: 'social',
    type: 'url',
    label: 'YouTube',
    default: null,
    public: true,
    maxLength: 255,
    placeholder: 'https://www.youtube.com/…',
  },

  // ── Référencement ──
  {
    key: 'seo.default_meta_title',
    group: 'seo',
    section: 'defaults',
    type: 'text',
    label: 'Titre par défaut du site',
    help: 'Utilisé quand une page ne définit pas son propre titre de référencement. 60 caractères au plus pour un affichage complet dans Google.',
    default: null,
    public: true,
    maxLength: 255,
  },
  {
    key: 'seo.default_meta_description',
    group: 'seo',
    section: 'defaults',
    type: 'textarea',
    label: 'Description par défaut du site',
    help: 'Utilisée quand une page ne définit pas la sienne. 160 caractères au plus pour un affichage complet dans Google.',
    default: null,
    public: true,
    maxLength: 500,
    rows: 3,
  },
  {
    /*
     * Le coupe-circuit du référencement.
     *
     * Baissé, le site sert un `robots.txt` qui interdit tout à tous les
     * robots — ce qu'attend une préproduction, et ce qui déréférence une
     * production en quelques jours. D'où l'avertissement porté par le champ
     * lui-même : le réglage est à un clic, le retour ne l'est pas.
     *
     * Il ne passe pas par la configuration du module `nuxt-robots`, qui est
     * figée au build et ne peut donc pas dépendre d'un réglage en base. Le
     * site obtient le même effet en posant `Disallow: /` sur tous les agents.
     */
    key: 'seo.robots_indexable',
    group: 'seo',
    section: 'robots',
    type: 'boolean',
    label: 'Autoriser l\'indexation du site',
    help: '⚠️ Décocher retire le site des moteurs de recherche en quelques jours. À réserver à une préproduction.',
    default: true,
    public: true,
  },
  {
    /*
     * Robots d'intelligence artificielle.
     *
     * La liste des agents visés est celle de `@nuxtjs/robots` — GPTBot,
     * ChatGPT-User, Claude-Web, CCBot, Google-Extended, PerplexityBot et une
     * dizaine d'autres. Elle est tenue à jour par le module : une case à
     * cocher vaut mieux qu'une saisie manuelle, qui se démoderait sans qu'on
     * s'en aperçoive et qu'une faute de frappe rendrait sans effet.
     *
     * Fermé par défaut : c'est un choix éditorial, pas une valeur technique.
     * Le contenu de l'association nourrit son référencement ; l'ouvrir ou non
     * à l'entraînement des modèles se décide, et le défaut le plus prudent est
     * de ne rien changer à l'existant.
     */
    key: 'seo.robots_block_ai',
    group: 'seo',
    section: 'robots',
    type: 'boolean',
    label: 'Bloquer les robots d\'intelligence artificielle',
    help: 'Interdit l\'accès à GPTBot, Claude-Web, CCBot, Google-Extended, PerplexityBot et aux autres robots d\'entraînement. Sans effet sur Google et Bing, qui restent libres d\'indexer le site.',
    default: false,
    public: true,
  },
  {
    /*
     * Robots sans intérêt pour le référencement : aspirateurs de contacts,
     * scanneurs de vulnérabilités, moissonneurs divers. Les bloquer allège la
     * charge du serveur sans rien coûter en visibilité.
     */
    key: 'seo.robots_block_nuisibles',
    group: 'seo',
    section: 'robots',
    type: 'boolean',
    label: 'Bloquer les robots nuisibles',
    help: 'Interdit l\'accès aux aspirateurs de contacts et scanneurs connus (PetalBot, Zoominfobot, Nuclei…). Sans effet sur les moteurs de recherche.',
    default: false,
    public: true,
  },
  {
    /*
     * Chemins interdits, un par ligne.
     *
     * S'ajoutent aux règles que le site tient déjà de lui-même — les pages
     * d'aperçu et de prévisualisation restent fermées, quoi qu'on saisisse
     * ici. Ce champ ouvre donc l'ajout, jamais le retrait.
     */
    key: 'seo.robots_disallow',
    group: 'seo',
    section: 'robots',
    type: 'textarea',
    label: 'Chemins à ne pas explorer',
    help: 'Un chemin par ligne, commençant par « / ». Exemple : /recherche. Les pages d\'aperçu du site sont déjà fermées, inutile de les rappeler.',
    default: null,
    public: true,
    maxLength: 2000,
    rows: 5,
    placeholder: '/recherche',
  },
  {
    /*
     * Chemins autorisés, en regard des chemins interdits.
     *
     * `Allow` l'emporte sur `Disallow` pour un chemin plus précis : c'est ce
     * qui permet de fermer une rubrique tout en gardant une page ouverte. Sans
     * ce champ, la seule façon d'y parvenir serait d'énumérer tout ce qu'on
     * ferme, à une exception près.
     */
    key: 'seo.robots_allow',
    group: 'seo',
    section: 'robots',
    type: 'textarea',
    label: 'Chemins à explorer malgré tout',
    help: 'Un chemin par ligne. Utile pour rouvrir une page à l\'intérieur d\'une rubrique fermée ci-dessus : le chemin le plus précis l\'emporte.',
    default: null,
    public: true,
    maxLength: 2000,
    rows: 3,
    placeholder: '/librairie/livre-blanc',
  },
  {
    /*
     * Règles écrites à la main, ajoutées telles quelles en fin de fichier.
     *
     * Les cases à cocher couvrent le courant ; ce champ couvre le reste — un
     * robot que la liste du module ignore encore, un `Crawl-delay`, une
     * directive qu'aucun réglage ne prévoit. Sans lui, le moindre cas
     * particulier demanderait une intervention sur le code.
     *
     * Le contenu n'est pas interprété : c'est sa raison d'être, et ce qui en
     * fait le champ le plus facile à mal écrire. D'où l'aperçu, qui montre le
     * fichier tel qu'il sera servi.
     */
    key: 'seo.robots_extra',
    group: 'seo',
    section: 'robots',
    type: 'textarea',
    label: 'Règles supplémentaires',
    help: 'Ajoutées telles quelles à la fin du fichier, sans vérification. Pour un robot absent des listes ci-dessus, ou une directive particulière.',
    default: null,
    public: true,
    maxLength: 4000,
    rows: 5,
    placeholder: 'User-agent: MonRobot\nDisallow: /',
  },

  // ── Bannière d'alerte ──
  {
    key: 'alert.active',
    group: 'alert',
    section: 'banner',
    type: 'boolean',
    label: 'Activer la bannière',
    default: false,
    public: true,
  },
  {
    key: 'alert.message',
    group: 'alert',
    section: 'banner',
    type: 'textarea',
    label: 'Message',
    default: null,
    public: true,
    maxLength: 500,
    rows: 2,
    placeholder: 'Fermeture exceptionnelle du 15 au 20 août',
  },
  {
    key: 'alert.type',
    group: 'alert',
    section: 'banner',
    type: 'select',
    label: 'Type',
    default: 'info',
    public: true,
    options: alertTypes,
  },
  {
    key: 'alert.link_label',
    group: 'alert',
    section: 'button',
    type: 'text',
    label: 'Libellé du bouton',
    help: 'Laisser vide pour une bannière sans bouton.',
    default: null,
    public: true,
    maxLength: 100,
    placeholder: 'En savoir plus',
  },
  {
    /*
     * Clé nouvelle, et non `alert.link_url` réutilisée : l'ancienne stockait
     * une adresse nue, celle-ci un objet `LinkValue`. Garder le nom aurait fait
     * relire une chaîne là où un JSON est attendu, sur une ligne déjà en base.
     * L'ancienne clé sort du registre — la ligne subsiste, inerte, et son
     * adresse est reprise au premier enregistrement (voir `migrateAlertLink`).
     */
    key: 'alert.link',
    group: 'alert',
    section: 'button',
    type: 'link',
    label: 'Destination',
    help: 'Une page du site, une adresse extérieure ou un fichier, avec le choix de l\'onglet d\'ouverture.',
    // Une bannière renvoie parfois vers un document — un avis, un formulaire à
    // remplir — que rien ne justifiait de recopier dans une page.
    modes: ['internal', 'external', 'media'],
    default: null,
    public: true,
  },
  {
    key: 'alert.starts_at',
    group: 'alert',
    section: 'period',
    type: 'datetime',
    label: 'Date de début',
    help: 'Optionnel. Avant cette date, la bannière reste masquée même si elle est activée.',
    default: null,
    public: true,
  },
  {
    key: 'alert.ends_at',
    group: 'alert',
    section: 'period',
    type: 'datetime',
    label: 'Date de fin',
    help: 'Optionnel. Après cette date, la bannière disparaît automatiquement.',
    default: null,
    public: true,
  },
  // ── Bandeau cookies ──
  /*
   * Le consentement lui-même — ce que le visiteur a répondu — n'est pas un
   * réglage : il vit dans le navigateur de chacun. Ce groupe ne décrit que la
   * demande, c'est-à-dire ce que le site affiche avant d'avoir la réponse.
   */
  {
    key: 'cookies.active',
    group: 'cookies',
    section: 'message',
    type: 'boolean',
    label: 'Afficher le bandeau',
    help: 'Désactivé, aucune demande n\'est faite : à ne laisser ainsi que si le site ne dépose aucun cookie non essentiel.',
    /*
     * Armé par défaut, sur tous les environnements.
     *
     * Le site charge Google Maps sur sa page de contact, qui dépose des cookies
     * tiers : la demande de consentement n'est donc pas optionnelle, et un
     * défaut à `false` la ferait manquer sur toute base neuve — celle de
     * production comprise, où l'oubli ne se verrait qu'après coup.
     *
     * Ce défaut ne concerne que les bases où la clé n'a jamais été écrite : une
     * décision prise depuis l'administration prime, dans un sens comme dans
     * l'autre.
     */
    default: true,
    public: true,
  },
  {
    key: 'cookies.title',
    group: 'cookies',
    section: 'message',
    type: 'text',
    label: 'Titre',
    help: 'Laisser vide pour un bandeau sans titre, réduit au seul message.',
    default: 'Nous respectons votre vie privée',
    public: true,
    maxLength: 100,
    placeholder: 'Nous respectons votre vie privée',
  },
  {
    key: 'cookies.message',
    group: 'cookies',
    section: 'message',
    type: 'textarea',
    label: 'Message',
    help: 'Dites à quoi servent les cookies déposés. Un texte vague fait un consentement fragile.',
    default: 'Ce site dépose des cookies pour mesurer son audience et améliorer votre navigation. '
      + 'Vous pouvez les accepter ou les refuser : votre choix sera conservé et restera modifiable à tout moment.',
    public: true,
    maxLength: 1000,
    rows: 4,
  },
  {
    key: 'cookies.accept_label',
    group: 'cookies',
    section: 'buttons',
    type: 'text',
    label: 'Libellé du bouton d\'acceptation',
    default: 'Tout accepter',
    public: true,
    maxLength: 60,
    placeholder: 'Tout accepter',
  },
  {
    key: 'cookies.refuse_label',
    group: 'cookies',
    section: 'buttons',
    type: 'text',
    label: 'Libellé du bouton de refus',
    help: 'Obligatoire : refuser doit rester possible en un seul geste, au même niveau qu\'accepter.',
    default: 'Tout refuser',
    public: true,
    maxLength: 60,
    placeholder: 'Tout refuser',
  },
  {
    key: 'cookies.policy_label',
    group: 'cookies',
    section: 'buttons',
    type: 'text',
    label: 'Libellé du lien vers la politique',
    help: 'Laisser vide, ainsi que la destination, pour un bandeau sans lien.',
    default: 'En savoir plus',
    public: true,
    maxLength: 60,
    placeholder: 'En savoir plus',
  },
  {
    /*
     * Un `link` plutôt qu'une `url` : la politique de confidentialité est une
     * page du site, et le lien interne suit son chemin si elle est renommée —
     * ce qu'une adresse figée ne ferait pas.
     */
    key: 'cookies.policy_link',
    group: 'cookies',
    section: 'buttons',
    type: 'link',
    label: 'Destination du lien',
    help: 'La page qui détaille les cookies déposés, en général la politique de confidentialité.',
    default: null,
    public: true,
  },
  {
    key: 'cookies.position',
    group: 'cookies',
    section: 'display',
    type: 'select',
    label: 'Emplacement',
    default: 'bottom',
    public: true,
    options: cookiePositions,
  },
  {
    key: 'cookies.duration_days',
    group: 'cookies',
    section: 'display',
    type: 'number',
    label: 'Durée de conservation d\'une acceptation (jours)',
    help: 'Passé ce délai, la question est reposée. Un refus, lui, est repris au bout de 90 jours — durée fixée dans le site, pour laisser au visiteur l\'occasion de changer d\'avis sans le solliciter trop souvent.',
    /*
     * 13 mois, le maximum admis pour un cookie de consentement. On retient le
     * plafond plutôt que les 6 mois recommandés parce que le refus a désormais
     * sa propre durée, bien plus courte : c'est lui qui protège le visiteur du
     * choix figé, et l'acceptation n'a plus de raison d'être écourtée.
     */
    default: 396,
    public: true,
    min: 1,
    max: 396,
  },

  // ── Scripts et intégrations ──
  /*
   * Quatre emplacements, au croisement de deux axes : **où** le code est inséré
   * (dans le `<head>`, ou juste avant la fermeture du `<body>`) et **à quelle
   * condition** (toujours, ou seulement après acceptation des cookies).
   *
   * La distinction de condition n'est pas un confort : un traceur chargé avant
   * le consentement dépose ses cookies sans l'avoir obtenu, ce qui est
   * précisément ce que le bandeau est censé empêcher. Séparer les deux jeux de
   * champs évite d'avoir à trier du code mêlé au moment de l'insertion — le
   * rédacteur a déjà fait ce tri en choisissant sa section.
   *
   * L'axe de position répond, lui, à une contrainte technique : une feuille de
   * style doit être connue avant le rendu, un script de mesure gagne à être
   * chargé en fin de page pour ne pas retarder l'affichage.
   */
  {
    key: 'scripts.active',
    group: 'scripts',
    section: 'always',
    type: 'boolean',
    label: 'Activer les scripts',
    help: 'Coupe les quatre emplacements d\'un seul geste, sans vider les champs. Utile quand un script casse le site : on rétablit d\'abord, on corrige ensuite.',
    default: true,
    public: true,
  },
  {
    key: 'scripts.head',
    group: 'scripts',
    section: 'always',
    type: 'code',
    label: 'Dans le <head>',
    help: 'Feuilles de style, polices, balises de vérification de propriété. Rien qui dépose un cookie : ce code s\'exécute avant tout consentement.',
    default: null,
    public: true,
    maxLength: 10_000,
    rows: 6,
    placeholder: '<link rel="stylesheet" href="https://…">',
  },
  {
    key: 'scripts.body_end',
    group: 'scripts',
    section: 'always',
    type: 'code',
    label: 'En fin de page',
    help: 'Inséré juste avant la fermeture du corps de page. À préférer pour tout ce qui n\'a pas besoin d\'agir avant l\'affichage.',
    default: null,
    public: true,
    maxLength: 10_000,
    rows: 6,
    placeholder: '<script src="https://…"></script>',
  },
  {
    key: 'scripts.consent_head',
    group: 'scripts',
    section: 'consent',
    type: 'code',
    label: 'Dans le <head>, après consentement',
    help: 'Inséré uniquement si le visiteur a accepté les cookies.',
    default: null,
    public: true,
    maxLength: 10_000,
    rows: 6,
    placeholder: '<!-- Google Analytics, Matomo… -->',
  },
  {
    key: 'scripts.consent_body_end',
    group: 'scripts',
    section: 'consent',
    type: 'code',
    label: 'En fin de page, après consentement',
    help: 'Inséré uniquement si le visiteur a accepté les cookies. L\'emplacement habituel des traceurs.',
    default: null,
    public: true,
    maxLength: 10_000,
    rows: 6,
    placeholder: '<!-- Meta Pixel, LinkedIn Insight… -->',
  },

  // ── Mode maintenance ──
  {
    key: 'maintenance.active',
    group: 'maintenance',
    type: 'boolean',
    label: 'Activer le mode maintenance',
    help: 'Les visiteurs voient une page d\'attente. L\'administration reste accessible.',
    default: false,
    public: true,
  },
  {
    key: 'maintenance.title',
    group: 'maintenance',
    type: 'text',
    label: 'Titre',
    help: 'Parle du bénéfice plutôt que du désagrément : une maintenance est choisie, pas subie.',
    default: 'Nous améliorons votre site',
    public: true,
    maxLength: 100,
    placeholder: 'Nous améliorons votre site',
  },
  {
    key: 'maintenance.message',
    group: 'maintenance',
    type: 'textarea',
    label: 'Message',
    help: 'Une durée, même approximative, vaut mieux qu\'une formule générique.',
    default: 'Une mise à jour est en cours pour améliorer nos services. Le site sera de retour très prochainement.',
    public: true,
    maxLength: 500,
    rows: 3,
    placeholder: 'Mise à jour en cours, retour prévu dans la journée.',
  },
] as const satisfies readonly SettingDefinition[]

/** Clé de réglage connue du registre. */
export type SettingKey = typeof siteSettings[number]['key']

/**
 * Type effectif d'un réglage, déduit de son `type` déclaré.
 *
 * C'est ce qui permet à `getSetting('cookies.duration_days')` de rendre un
 * `number | null` et à `getSetting('alert.active')` un `boolean`, sans que
 * l'appelant ait à convertir ni à supposer.
 */
type ValueOfType<T extends SettingType>
  = T extends 'number' ? number | null
    : T extends 'boolean' ? boolean
      : string | null

export type SettingValueOf<K extends SettingKey>
  = Extract<typeof siteSettings[number], { key: K }> extends { type: infer T extends SettingType }
    ? ValueOfType<T>
    : never

/** Index par clé, pour retrouver une définition en temps constant. */
const definitionsByKey = new Map<string, SettingDefinition>(
  siteSettings.map(setting => [setting.key, setting as SettingDefinition]),
)

export function getSettingDefinition(key: string): SettingDefinition | undefined {
  return definitionsByKey.get(key)
}

/** Vrai si la clé appartient au registre. */
export function isKnownSettingKey(key: string): key is SettingKey {
  return definitionsByKey.has(key)
}

/** Définitions d'un groupe, dans l'ordre d'affichage du formulaire. */
export function getGroupSettings(group: string): SettingDefinition[] {
  return (siteSettings as readonly SettingDefinition[]).filter(setting => setting.group === group)
}

export function isKnownSettingGroup(group: string): group is SettingGroupKey {
  return settingGroupKeys.includes(group as SettingGroupKey)
}

export function getGroupDefinition(group: string): SettingGroupDefinition | undefined {
  return (settingGroups as readonly SettingGroupDefinition[]).find(entry => entry.key === group)
}

/**
 * Valeurs par défaut d'un groupe, ou de tout le registre.
 *
 * Sert de base à la lecture : une clé jamais enregistrée n'a pas de ligne en
 * base, et c'est ici qu'elle trouve sa valeur.
 */
export function getSettingDefaults(group?: string): Record<string, SettingValue> {
  const source = group ? getGroupSettings(group) : (siteSettings as readonly SettingDefinition[])
  return Object.fromEntries(source.map(setting => [setting.key, setting.default]))
}

/**
 * Retire le préfixe de groupe d'une clé (`social.facebook` → `facebook`).
 *
 * L'API publique sert les réglages regroupés : répéter le groupe dans chaque
 * clé de l'objet serait redondant.
 */
export function stripGroupPrefix(key: string): string {
  const separator = key.indexOf('.')
  return separator === -1 ? key : key.slice(separator + 1)
}
